import React, { useState, useEffect } from 'react'
import { useSlider } from '../../context/SliderContext'
import FileUpload from './FileUpload'
import SlideCard from './SlideCard'
import { getImageUrl } from '../../utils/imageUtils'
import { 
  Upload, 
  Search, 
  Trash2, 
  Edit, 
  Eye, 
  Plus, 
  Image as ImageIcon, 
  Video, 
  Filter,
  Save,
  X,
  ArrowUp,
  ArrowDown,
  Move,
  Settings,
  RotateCcw
} from 'lucide-react'

const SliderManagement = () => {
  const {
    sliderItems,
    addSlide,
    updateSlide,
    deleteSlide,
    deleteSlides,
    toggleSlideActive,
    moveSlide,
    resetToDefault,
    getStats
  } = useSlider()

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedItems, setSelectedItems] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingSlide, setEditingSlide] = useState(null)
  const [previewMode, setPreviewMode] = useState(false)
  const [imageFile, setImageFile] = useState(null) // Store actual file
  const [deletingIds, setDeletingIds] = useState([]) // Track slides being deleted

  const [formData, setFormData] = useState({
    title: '',
    image: '',
    caption: '',
    buttonText: '',
    buttonLink: '',
    overlayOpacity: 20,
    textPosition: 'center',
    isActive: true
  })

  // Handle file upload - receives just the File object from FileUpload component
  const handleFileUpload = (file) => {
    if (file) {
      setImageFile(file) // Store the actual File object
      // Create a preview URL for display
      const previewUrl = URL.createObjectURL(file)
      setFormData(prev => ({
        ...prev,
        image: previewUrl // Store preview URL for display
      }))
    } else {
      // File was removed
      setImageFile(null)
      setFormData(prev => ({
        ...prev,
        image: ''
      }))
    }
  }

  const filteredItems = sliderItems.filter(item => 
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.caption.toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => a.order - b.order)

  const stats = getStats()

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    )
  }

  const handleSelectAll = () => {
    if (selectedItems.length === filteredItems.length) {
      setSelectedItems([])
    } else {
      setSelectedItems(filteredItems.map(item => item.id))
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) return

    // Filter out already-deleted slides and slides being deleted
    const existingSlides = selectedItems.filter(id => 
      sliderItems.find(slide => slide._id === id) && !deletingIds.includes(id)
    )

    if (existingSlides.length === 0) {
      alert('No valid slides to delete')
      setSelectedItems([])
      return
    }

    const confirmDelete = window.confirm(`Are you sure you want to delete ${existingSlides.length} slide(s)?`)
    if (confirmDelete) {
      // Add all to deleting state
      setDeletingIds(prev => [...prev, ...existingSlides])
      
      const result = await deleteSlides(existingSlides)
      setSelectedItems([])
      
      // Remove all from deleting state
      setDeletingIds(prev => prev.filter(id => !existingSlides.includes(id)))
      
      if (!result.success) {
        alert(result.message || 'Failed to delete slides')
      }
    }
  }

  const handleAddNew = () => {
    setEditingSlide(null)
    setImageFile(null) // Reset image file
    setFormData({
      title: '',
      image: '',
      caption: '',
      buttonText: '',
      buttonLink: '',
      overlayOpacity: 20,
      textPosition: 'center',
      isActive: true
    })
    setIsModalOpen(true)
  }

  const handleEdit = (slide) => {
    setEditingSlide(slide)
    setImageFile(null) // Reset image file when editing
    setFormData({
      title: slide.title,
      image: getImageUrl(slide.image), // Use getImageUrl to get full backend URL
      caption: slide.caption,
      buttonText: slide.buttonText,
      buttonLink: slide.buttonLink,
      overlayOpacity: slide.overlayOpacity,
      textPosition: slide.textPosition,
      isActive: slide.isActive
    })
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    // Cleanup blob URL if it exists
    if (formData.image && formData.image.startsWith('blob:')) {
      URL.revokeObjectURL(formData.image)
    }
    setIsModalOpen(false)
    setImageFile(null)
  }

  const handleSave = () => {
    // Validate required fields
    if (!formData.title || formData.title.trim() === '') {
      alert('Please enter a title for the slide')
      return
    }

    // For new slides, image is required
    if (!editingSlide && !imageFile) {
      alert('Please select an image for the slide')
      return
    }

    // Create FormData object for multipart/form-data
    const data = new FormData()
    
    // Add text fields
    data.append('title', formData.title)
    if (formData.caption) data.append('caption', formData.caption)
    if (formData.buttonText) data.append('buttonText', formData.buttonText)
    if (formData.buttonLink) data.append('buttonLink', formData.buttonLink)
    data.append('overlayOpacity', formData.overlayOpacity)
    data.append('textPosition', formData.textPosition)
    data.append('isActive', formData.isActive)
    
    // Add image file if provided
    if (imageFile) {
      data.append('image', imageFile)
      console.log('📎 Adding image to FormData:', {
        name: imageFile.name,
        type: imageFile.type,
        size: imageFile.size
      })
    }
    
    if (editingSlide) {
      // Update existing slide
      updateSlide(editingSlide._id, data)
    } else {
      // Add new slide
      addSlide(data)
    }
    
    // Cleanup blob URL if it exists
    if (formData.image && formData.image.startsWith('blob:')) {
      URL.revokeObjectURL(formData.image)
    }
    
    // Reset form
    setIsModalOpen(false)
    setImageFile(null)
  }

  const handleDelete = async (slideId) => {
    // Prevent deletion if already deleted or being deleted
    if (!sliderItems.find(slide => slide._id === slideId) || deletingIds.includes(slideId)) {
      console.warn('Slide already deleted or being deleted:', slideId)
      return
    }

    const confirmDelete = window.confirm('Are you sure you want to delete this slide?')
    if (confirmDelete) {
      // Add to deleting state
      setDeletingIds(prev => [...prev, slideId])
      
      const result = await deleteSlide(slideId)
      
      // Remove from deleting state
      setDeletingIds(prev => prev.filter(id => id !== slideId))
      
      if (!result.success) {
        alert(result.message || 'Failed to delete slide')
      }
    }
  }

  const handleToggleActive = (slideId) => {
    toggleSlideActive(slideId)
  }

  const handleMoveUp = (slideId) => {
    moveSlide(slideId, 'up')
  }

  const handleMoveDown = (slideId) => {
    moveSlide(slideId, 'down')
  }

  const handleResetToDefault = () => {
    const confirmReset = window.confirm('This will reset the slider to default images. Are you sure?')
    if (confirmReset) {
      resetToDefault()
      setSelectedItems([])
    }
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hero Slider Management</h1>
          <p className="text-gray-600 mt-1">Manage the homepage hero slider images and content</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center gap-3">
          {selectedItems.length > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete ({selectedItems.length})
            </button>
          )}
         
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            {previewMode ? 'Edit Mode' : 'Preview'}
          </button>
          <button
            onClick={handleAddNew}
            className="bg-primary-purple text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-magenta transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Slide
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="search"
              placeholder="Search slides..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-purple focus:border-primary-purple"
            />
          </div>

          {/* Select All */}
        </div>
      </div>

      {previewMode ? (
        /* Preview Mode */
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Slider Preview</h3>
            <p className="text-sm text-gray-600">This is how the slider will appear on the homepage</p>
          </div>
          <div className="relative h-96 bg-gray-100">
            {filteredItems.filter(item => item.isActive).length > 0 ? (
              <div className="relative h-full">
                <img
                  src={getImageUrl(filteredItems.filter(item => item.isActive)[0].image)}
                  alt={filteredItems.filter(item => item.isActive)[0].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                  <div className="text-center text-white">
                    <h2 className="text-3xl font-bold mb-2">Shri V.D. Patel Madhyamik School</h2>
                    <p className="text-lg mb-4">{filteredItems.filter(item => item.isActive)[0].caption}</p>
                    <button className="bg-primary-purple text-white px-6 py-3 rounded-lg font-semibold">
                      {filteredItems.filter(item => item.isActive)[0].buttonText}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <ImageIcon className="w-12 h-12 mx-auto mb-2" />
                  <p>No active slides to preview</p>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Edit Mode - Slider List */
        <div className="space-y-4">
          {filteredItems.map((slide, index) => (
            <SlideCard
              key={slide._id}
              slide={slide}
              index={index}
              totalSlides={filteredItems.length}
              isSelected={selectedItems.includes(slide._id)}
              isDeleting={deletingIds.includes(slide._id)}
              onSelect={() => handleSelectItem(slide._id)}
              onEdit={() => handleEdit(slide)}
              onDelete={() => handleDelete(slide._id)}
              onToggleActive={() => handleToggleActive(slide._id)}
              onMoveUp={() => handleMoveUp(slide._id)}
              onMoveDown={() => handleMoveDown(slide._id)}
            />
          ))}

          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-500 text-lg">No slides found</div>
              <p className="text-gray-400 text-sm mt-1">Try adjusting your search or add a new slide</p>
            </div>
          )}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
          <div className="text-sm text-gray-600">Total Slides</div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          <div className="text-sm text-gray-600">Active Slides</div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="text-2xl font-bold text-orange-600">{stats.inactive}</div>
          <div className="text-sm text-gray-600">Inactive Slides</div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="text-2xl font-bold text-purple-600">{stats.activeRate}%</div>
          <div className="text-sm text-gray-600">Active Rate</div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingSlide ? 'Edit Slide' : 'Add New Slide'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-purple focus:border-primary-purple"
                    placeholder="Enter slide title"
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Slide Image</label>
                  <FileUpload
                    onFileSelect={handleFileUpload}
                    currentImageUrl={formData.image}
                    accept={{ 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] }}
                    maxSize={5 * 1024 * 1024} // 5MB
                  />
                </div>

                {/* Caption */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Caption</label>
                  <textarea
                    value={formData.caption}
                    onChange={(e) => setFormData({...formData, caption: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-purple focus:border-primary-purple"
                    placeholder="Enter slide caption"
                  />
                </div>

                {/* Button Text & Link */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Button Text</label>
                    <input
                      type="text"
                      value={formData.buttonText}
                      onChange={(e) => setFormData({...formData, buttonText: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-purple focus:border-primary-purple"
                      placeholder="Explore More"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Button Link</label>
                    <input
                      type="text"
                      value={formData.buttonLink}
                      onChange={(e) => setFormData({...formData, buttonLink: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-purple focus:border-primary-purple"
                      placeholder="#section or /page"
                    />
                  </div>
                </div>

                {/* Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Text Position</label>
                    <select
                      value={formData.textPosition}
                      onChange={(e) => setFormData({...formData, textPosition: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-purple focus:border-primary-purple"
                    >
                      <option value="center">Center</option>
                      <option value="left">Left</option>
                      <option value="right">Right</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Overlay Opacity (%)</label>
                    <input
                      type="range"
                      min="0"
                      max="50"
                      value={formData.overlayOpacity}
                      onChange={(e) => setFormData({...formData, overlayOpacity: parseInt(e.target.value)})}
                      className="w-full"
                    />
                    <div className="text-sm text-gray-600 mt-1">{formData.overlayOpacity}%</div>
                  </div>
                </div>

                {/* Active Status */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                      className="rounded border-gray-300 text-primary-purple focus:ring-primary-purple"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active (show in slider)</span>
                  </label>
                </div>
                
                {/* Action Buttons */}
                <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleSave}
                    className="bg-primary-purple text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-magenta transition-colors flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {editingSlide ? 'Update Slide' : 'Add Slide'}
                  </button>
                  <button
                    onClick={handleCloseModal}
                    className="bg-gray-100 text-gray-700 px-6 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SliderManagement